import React from 'react';

export interface SetSquareSvgProps {
  width?: number;
  /**
   * Mirror the set square:
   *   false (default) → right angle at bottom-right
   *   true            → right angle at bottom-left
   * Geometry is recomputed so graduation labels are always readable.
   */
  flipped?: boolean;
}

// ── Design constants ──────────────────────────────────────────────────────────
const PAD_SIDE = 22;          // label space on BOTH sides (symmetric viewBox)
const PAD_T    = 8;
const LEG      = 160;         // leg length (12 cm)
const CM       = 12;
const CM_PX    = LEG / CM;    // px/cm ≈ 13.33

// Hypotenuse of a 45-45-90 triangle with legs of CM cm ≈ CM*√2 cm.
// Rounded to the nearest integer for a clean tick count.
const HYP_CM   = Math.round(CM * Math.SQRT2);  // 17
const BOTTOM_Y = PAD_T + LEG;         // 168 — y of the horizontal base
const VW       = PAD_SIDE * 2 + LEG;  // 204 — symmetric: labels fit on either side
const VH       = BOTTOM_Y + 20;       // 188
/** Rendered height ÷ width — use this to reserve layout space without duplicating viewBox values. */
export const SET_SQUARE_ASPECT_RATIO = VH / VW;

const C_BORDER = '#705DC0';
const C_FILL   = '#f5f2ff';
const C_TICK   = '#4a3a9e';
const C_LABEL  = '#2d1f7a';

// Fixed base corners — same in both modes
const AX = PAD_SIDE;        // 22  — bottom-left
const BX = PAD_SIDE + LEG;  // 182 — bottom-right

/**
 * 45-45-90 set square.
 *
 * Normal  (flipped=false): right angle at bottom-right, apex top-right.
 * Flipped (flipped=true):  right angle at bottom-left,  apex top-left.
 *
 * Geometry is recomputed for each mode — no CSS mirror transform.
 * Graduation labels are always upright and outside the triangle body.
 */
export const SetSquareSvg: React.FC<SetSquareSvgProps> = ({ width = 160, flipped = false }) => {
  // Apex (top corner) — changes side with flipped
  const CX = flipped ? AX : BX;
  const CY = PAD_T;  // 8

  // Right-angle corner and per-mode directions
  const raX         = flipped ? AX : BX;
  const raDir       = flipped ? +1 : -1;   // toward interior along X from 90° corner
  const vertX       = flipped ? AX : BX;   // x of vertical leg
  const tickXDir    = flipped ? +1 : -1;   // tick points inward
  const labelX      = flipped ? vertX - 7 : vertX + 7;
  const labelAnchor = flipped ? 'end' : 'start';

  const ticks: React.ReactNode[] = [];

  // ── Bottom edge (AX → BX): identical in both modes ───────────────────────
  for (let i = 0; i <= CM; i++) {
    const x     = AX + i * CM_PX;
    const isMaj = i % 5 === 0;
    const h     = isMaj ? 10 : i % 2 === 0 ? 6 : 3.5;
    ticks.push(
      <line key={`h${i}`}
        x1={x} y1={BOTTOM_Y} x2={x} y2={BOTTOM_Y - h}
        stroke={C_TICK} strokeWidth={isMaj ? 1.3 : 0.7}
      />
    );
    if (i > 0 && i < CM) {
      ticks.push(
        <text key={`hl${i}`}
          x={x} y={BOTTOM_Y + 13}
          textAnchor="middle"
          fontSize={isMaj ? 7.5 : 6}
          fontWeight={isMaj ? '700' : '400'}
          fontFamily="Arial, sans-serif"
          fill={C_LABEL}
        >{i}</text>
      );
    }
  }

  // ── Vertical leg: right edge (normal) or left edge (flipped) ─────────────
  for (let i = 0; i <= CM; i++) {
    const y     = BOTTOM_Y - i * CM_PX;
    const isMaj = i % 5 === 0;
    const w     = isMaj ? 10 : i % 2 === 0 ? 6 : 3.5;
    ticks.push(
      <line key={`v${i}`}
        x1={vertX} y1={y} x2={vertX + tickXDir * w} y2={y}
        stroke={C_TICK} strokeWidth={isMaj ? 1.3 : 0.7}
      />
    );
    if (i > 0 && i < CM) {
      ticks.push(
        <text key={`vl${i}`}
          x={labelX} y={y + 2.5}
          textAnchor={labelAnchor as 'start' | 'end'}
          fontSize={isMaj ? 7.5 : 6}
          fontWeight={isMaj ? '700' : '400'}
          fontFamily="Arial, sans-serif"
          fill={C_LABEL}
        >{i}</text>
      );
    }
  }

  // ── Hypotenuse ticks ──────────────────────────────────────────────────────
  // Normal:  A(22,168)→C(182,8)   inward perpendicular = (+x,+y) toward B(182,168)
  // Flipped: B(182,168)→C(22,8)   inward perpendicular = (-x,+y) toward A(22,168)
  const hypX1 = flipped ? BX : AX;
  const hdx   = (CX - hypX1) / HYP_CM;
  const hdy   = (CY - BOTTOM_Y) / HYP_CM;
  const hlen  = Math.sqrt(hdx * hdx + hdy * hdy);
  const hnx   = flipped ?  hdy / hlen : -hdy / hlen;
  const hny   = flipped ? -hdx / hlen :  hdx / hlen;

  const hypTicks: React.ReactNode[] = [];
  for (let i = 1; i < HYP_CM; i++) {
    const x     = hypX1 + i * hdx;
    const y     = BOTTOM_Y + i * hdy;
    const isMaj = i % 5 === 0;
    const t     = isMaj ? 7 : i % 2 === 0 ? 4.5 : 2.5;
    hypTicks.push(
      <line key={`hyp${i}`}
        x1={x} y1={y} x2={x + hnx * t} y2={y + hny * t}
        stroke={C_TICK} strokeWidth={isMaj ? 1.3 : 0.7}
      />
    );
  }

  return (
    <svg
      width={width}
      viewBox={`0 0 ${VW} ${VH}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block' }}
      role="img"
      aria-label="Set Square"
    >
      {/* Triangle body */}
      <polygon
        points={`${AX},${BOTTOM_Y} ${BX},${BOTTOM_Y} ${CX},${CY}`}
        fill={C_FILL}
        stroke={C_BORDER}
        strokeWidth={1.8}
        strokeLinejoin="round"
      />

      {/* Graduations */}
      {ticks}

      {/* Right-angle symbol — small square at the 90° corner, opening toward interior */}
      <polyline
        points={`${raX + raDir * 13},${BOTTOM_Y} ${raX + raDir * 13},${BOTTOM_Y - 13} ${raX},${BOTTOM_Y - 13}`}
        fill="none"
        stroke={C_BORDER}
        strokeWidth={1.3}
      />

      {/* Hypotenuse ticks */}
      {hypTicks}
    </svg>
  );
};
